import { Grid } from '@mui/material'
import { DataGridPremium, GridColumnMenu, GridColumnMenuProps, GridCsvExportOptions, GridEventListener, useGridApiRef } from '@mui/x-data-grid-premium'
import { useContext, useEffect, useState } from 'react'
import parse from 'html-react-parser'
import { Taxon, taxonomicRankNames } from './Taxonomy.service'
import { ExpandState } from './Taxonomy'
import { highlightKeyword } from '../../../../components/SearchText'
import { SampleDataContext } from '../../../../context/CustomContext'

export interface HierarchyGridProps {
    taxonomicProfile: any
    taxonomicHierarchy: Taxon[]
    expandState: ExpandState
    isExpanded: boolean
    searchTerm: string
    loading: boolean
    setIsExpanded: (isExpanded: boolean) => void
}

export function HierarchyGrid(props: HierarchyGridProps) {
    const { taxonomicProfile, taxonomicHierarchy, expandState, isExpanded, searchTerm, loading, setIsExpanded } = props
    const { triggerExport, setTriggerExport } = useContext(SampleDataContext)

    const apiRef = useGridApiRef()

    const initialState = initialStateBuilder(INITIAL_COLUMNS_VISIBILITY)
    const builtColumns = buildColumns(taxonomicProfile, searchTerm)

    const dataReady = taxonomicProfile !== undefined

    const isGroupExpandedByDefault = () => {
        return isExpanded
    }

    useEffect(() => {
        if (expandState !== ExpandState.neutral) {
            setIsExpanded(expandState === ExpandState.expand)
        }
    }, [expandState])

    useEffect(() => {
        if (triggerExport && taxonomicProfile?.id) {
            const options: GridCsvExportOptions = {
                fileName: `TaxonomicHierarchy-${taxonomicProfile?.id}`,
                fields: ['rank', 'name', 'abundance', 'readCount'],
            }
            apiRef.current.exportDataAsCsv(options)
            setTriggerExport(false)
        }
    }, [apiRef, triggerExport, setTriggerExport, taxonomicProfile])

    const onRowClick: GridEventListener<'rowClick'> = (params) => {
        if ((params.id as string).startsWith('auto-generated')) {
            const rowNode = apiRef.current.getRowNode(params.id)
            if (rowNode && rowNode.type === 'group') {
                apiRef.current.setRowChildrenExpansion(params.id, !rowNode.childrenExpanded)
            }
        }
    }

    function CustomColumnMenu(props: GridColumnMenuProps) {
        return (
            <GridColumnMenu
                {...props}
                slots={{
                    columnMenuGroupingItem: null,
                    columnMenuPinningItem: null,
                    columnMenuColumnsItem: null,
                }}
            />
        )
    }

    return (
        <Grid>
            <DataGridPremium
                getRowId={(r) => r.id}
                apiRef={apiRef}
                autoHeight
                loading={loading}
                pagination
                pageSizeOptions={[50, 100]}
                sortingOrder={['asc', 'desc']}
                rows={(dataReady && taxonomicHierarchy) || []}
                columns={builtColumns}
                initialState={initialState}
                onRowClick={onRowClick}
                isGroupExpandedByDefault={isGroupExpandedByDefault}
                disableRowSelectionOnClick
                groupingColDef={{
                    availableAggregationFunctions: [],
                    headerName: 'Taxon name',
                    leafField: 'name',
                    flex: 1,
                    minWidth: 500,
                }}
                slots={{
                    noRowsOverlay: () => <NoDataOverlay />,
                    columnMenu: CustomColumnMenu,
                }}
                sx={{
                    '--DataGrid-cellOffsetMultiplier': 2,
                    '.MuiDataGrid-cell:not(:has(.MuiDataGrid-groupingCriteriaCell))': {
                        '&:has(.name)': {
                            paddingLeft: '170px',
                        },
                    },
                    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                        outline: 'none',
                    },
                    '.MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 'bold',
                    },
                    '.keyword': {
                        backgroundColor: '#FFEBFF',
                        padding: '8px 0',
                    },
                    '.MuiDataGrid-row:has([data-testid="KeyboardArrowRightIcon"])': {
                        cursor: 'pointer',
                    },
                    '.MuiDataGrid-row:has([data-testid="ExpandMoreIcon"])': {
                        cursor: 'pointer',
                        '&:has(.level-0)': {
                            borderBottom: '2px solid rgba(27, 73, 212, 1)',
                        },
                    },
                    '.MuiDataGrid-row:not(:has(.level-0)):not(:has(.name))': {
                        backgroundImage: 'linear-gradient(rgba(240, 240, 255, 0.35), rgba(240, 240, 255, 0.1))',
                    },
                    '.MuiDataGrid-columnHeaders': {
                        backgroundImage: 'linear-gradient(rgba(240, 240, 255, 0.35), rgba(240, 240, 255, 0.1))',
                    },
                    '& .project-name': {
                        fontWeight: 'bold',
                    },
                    borderTop: 'none',
                    borderRight: 'none',
                    borderLeft: 'none',
                    borderWidth: '1px',
                    marginTop: '15px',
                    width: '100%',
                }}
            />
        </Grid>
    )
}

const buildColumns = (taxonomicProfile: any, searchTerm: string) => {
    const columns = [
        {
            field: 'rank',
            headerName: 'Rank',
            width: 200,
            availableAggregationFunctions: [],
            valueGetter: ({ rowNode }) => rankGetter(rowNode),
        },
        {
            field: 'name',
            headerName: 'Name',
            renderCell: (params) => renderHTML(params.row.name, searchTerm, 'name'),
        },
        {
            field: 'abundance',
            headerName: 'Abundance (%)',
            width: 200,
            availableAggregationFunctions: [],
            valueGetter: (params) => abundanceGetter(params, taxonomicProfile),
        },
        {
            field: 'readCount',
            headerName: 'No. reads',
            flex: 2,
            availableAggregationFunctions: [],
            valueGetter: (params) => readCountGetter(params, taxonomicProfile),
        },
        {
            field: 'domain',
            headerName: 'Domain',
            renderCell: (params) => renderGroup(params, searchTerm, 0),
        },
        {
            field: 'phylum',
            headerName: 'Phylum',
            renderCell: (params) => renderGroup(params, searchTerm, 1),
        },
        {
            field: 'class',
            headerName: 'Class',
            renderCell: (params) => renderGroup(params, searchTerm, 2),
        },
        {
            field: 'order',
            headerName: 'Order',
            renderCell: (params) => renderGroup(params, searchTerm, 3),
        },
        {
            field: 'family',
            headerName: 'Family',
            renderCell: (params) => renderGroup(params, searchTerm, 4),
        },
        {
            field: 'genus',
            headerName: 'Genus',
            renderCell: (params) => renderGroup(params, searchTerm, 5),
        },
        {
            field: 'species',
            headerName: 'Species',
            renderCell: (params) => renderGroup(params, searchTerm, 6),
        },
    ]
    return columns
}

const rankGetter = (rowNode: any): string => {
    const rank: string = rowNode.groupingField || 'asv'
    return taxonomicRankNames[rank]
}

const abundanceGetter = (params: any, taxonomicProfile: any): number => {
    const abundance = rankPropertyGetter(params, taxonomicProfile, 'abundance')
    return Math.ceil(abundance * 1000) / 1000
}

const readCountGetter = (params: any, taxonomicProfile: any): string => {
    const readCount = rankPropertyGetter(params, taxonomicProfile, 'readCount')
    return readCount.toLocaleString('en-US')
}

const rankPropertyGetter = (params: any, taxonomicProfile: any, rankProperty: string): number => {
    let value = params.value
    if (params.rowNode.type === 'group') {
        const taxaArray: any[] = taxonomicProfile[params.rowNode.groupingField].taxa
        value = taxaArray.find((x) => x.name === params.rowNode.groupingKey)?.[rankProperty] || ''
    }

    return Math.ceil(value * 1000) / 1000
}

const renderGroup = (params: any, searchTerm: string, level: number) => {
    return renderHTML(params.value, searchTerm, `group level-${level}`)
}

const renderHTML = (value: string, searchTerm: string, classes?: string) => {
    if (!value) {
        return null
    }

    const htmlValue = highlightKeyword(value, searchTerm)
    return <div className={classes}>{parse(htmlValue)}</div>
}

const initialStateBuilder = (columnVisibilityModel: any) => {
    return {
        pagination: {
            paginationModel: { pageSize: 50, page: 0 },
        },
        rowGrouping: {
            model: ['domain', 'phylum', 'class', 'order', 'family', 'genus', 'species'],
        },
        columns: {
            columnVisibilityModel: columnVisibilityModel,
        },
    }
}

const INITIAL_COLUMNS_VISIBILITY = {
    domain: false,
    phylum: false,
    class: false,
    order: false,
    family: false,
    genus: false,
    species: false,
    name: false,
}

const NoDataOverlay = () => {
    const [show, setShow] = useState(false)

    useEffect(() => {
        const timeout = setTimeout(() => setShow(true), 300)
        return () => clearTimeout(timeout)
    }, [])

    if (!show) return null
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
            }}
        >
            <p>No data found</p>
        </div>
    )
}
